import { useRef, useState, useEffect, useCallback } from "react";

const MIN = 1;
const MAX = 5;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export function usePinchZoom() {
  const wrapRef = useRef(null);
  const g = useRef({ scale: 1, tx: 0, ty: 0, mode: null, startDist: 0, startScale: 1, sx: 0, sy: 0, stx: 0, sty: 0, cx: 0, cy: 0, lastTap: 0 });
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });

  const apply = useCallback(() => {
    const { scale, tx, ty } = g.current;
    setView({ scale, tx, ty });
  }, []);

  const clampPan = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const maxX = ((g.current.scale - 1) * width) / 2;
    const maxY = ((g.current.scale - 1) * height) / 2;
    g.current.tx = clamp(g.current.tx, -maxX, maxX);
    g.current.ty = clamp(g.current.ty, -maxY, maxY);
  }, []);

  const center = () => {
    const r = wrapRef.current.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };

  const zoomTo = useCallback((newScale, px, py) => {
    const c = center();
    const cx = px - c.x;
    const cy = py - c.y;
    const s = g.current.scale;
    const ns = clamp(newScale, MIN, MAX);
    g.current.tx = cx - (cx - g.current.tx) * (ns / s);
    g.current.ty = cy - (cy - g.current.ty) * (ns / s);
    g.current.scale = ns;
    if (ns <= MIN) { g.current.tx = 0; g.current.ty = 0; }
    clampPan();
    apply();
  }, [apply, clampPan]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        g.current.mode = "pinch";
        g.current.startDist = dist(e.touches);
        g.current.startScale = g.current.scale;
        const m = mid(e.touches);
        g.current.cx = m.x; g.current.cy = m.y;
      } else if (e.touches.length === 1) {
        g.current.mode = "pan";
        g.current.sx = e.touches[0].clientX;
        g.current.sy = e.touches[0].clientY;
        g.current.stx = g.current.tx;
        g.current.sty = g.current.ty;
      }
    };

    const onTouchMove = (e) => {
      if (g.current.mode === "pinch" && e.touches.length === 2) {
        e.preventDefault();
        const ratio = dist(e.touches) / (g.current.startDist || 1);
        zoomTo(g.current.startScale * ratio, g.current.cx, g.current.cy);
      } else if (g.current.mode === "pan" && e.touches.length === 1 && g.current.scale > 1) {
        e.preventDefault();
        g.current.tx = g.current.stx + (e.touches[0].clientX - g.current.sx);
        g.current.ty = g.current.sty + (e.touches[0].clientY - g.current.sy);
        clampPan();
        apply();
      }
    };

    const onTouchEnd = (e) => {
      if (g.current.mode !== "pinch" && e.changedTouches.length === 1 && (!e.touches || e.touches.length === 0)) {
        const now = Date.now();
        if (now - g.current.lastTap < 280) {
          const t = e.changedTouches[0];
          zoomTo(g.current.scale > 1 ? MIN : 2.5, t.clientX, t.clientY);
          g.current.lastTap = 0;
        } else {
          g.current.lastTap = now;
        }
      }
      if (!e.touches || e.touches.length === 0) {
        if (g.current.scale <= MIN) { g.current.tx = 0; g.current.ty = 0; apply(); }
        g.current.mode = null;
      }
    };

    const onWheel = (e) => {
      e.preventDefault();
      zoomTo(g.current.scale * (e.deltaY < 0 ? 1.15 : 0.87), e.clientX, e.clientY);
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("wheel", onWheel);
    };
  }, [zoomTo, clampPan, apply]);

  const onMouseDown = (e) => {
    if (g.current.scale <= 1) return;
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, stx = g.current.tx, sty = g.current.ty;
    const move = (ev) => { g.current.tx = stx + (ev.clientX - sx); g.current.ty = sty + (ev.clientY - sy); clampPan(); apply(); };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return { wrapRef, view, gestureRef: g, zoomTo, onMouseDown, MIN };
}
